/**
 * system.js — screens when something refused.
 *
 * FOUND-2: liquid type/scroll + typed error grammar; soft operator copy (no · Live spam).
 * Soft≠Live: failure actions do real work (reload, re-auth, open a Live desk).
 */
import { useState, useEffect } from "react";
import { AnimatePresence, MotionConfig } from "framer-motion";
import { html, M, Ico, Alert, SOFT, SPRING, go } from "./ui.js";
import { Art } from "./sysart.js";
import * as D from "./data.js";

function hardReload() {
  try { location.reload(); } catch (_) { go("/ops"); }
}

export default function System({ which, toast }){
  const list = D.SYSTEM_STATES;
  const at = list.find(s=>s.id===which) || list[0];
  return html`
    <${MotionConfig} reducedMotion="user">
      <div className="sheet sys sys--liquid desk-craft"
        data-cmc-page="page149-system"
        data-cmc-found="found-2"
        data-sys=${at.id}
        data-viewport-fill="1">
        <${AnimatePresence} mode="wait">
          <${M.div} key=${at.id} className="sys__in" initial=${{ opacity:0, y:16 }} animate=${{ opacity:1, y:0 }}
              exit=${{ opacity:0, y:-12 }} transition=${SOFT}>
            <div className="sys__art"><${Art} kind=${at.art} code=${at.code}/></div>
            ${at.code!=="—" && html`<div className="sys__code" data-tone=${at.tone}>${at.code}</div>`}
            <p className="sys__eyebrow">System</p>
            <h1 className="sys__title">${at.title}</h1>
            <p>${at.say}</p>
            <div className="sys__mean"><i/><span>${at.mean}</span></div>
            ${at.id==="403" ? html`
              <div style=${{ maxWidth:420, margin:"14px auto 0", textAlign:"left" }}>
                <${Alert} tone="info" title="Access is decided on the account"
                  say="There is no soft “ask for access” toast. Open Profile to confirm your role, or return to Operations. Grants stay with Platform staff."/>
              </div>` : null}
            <div className="sys__acts">${actionsFor(at, toast)}</div>
          <//>
        <//>
      </div>
    <//>`;
}

function actionsFor(s, toast){
  const back  = html`<${M.button} key="b" className="btn btn--ghost btn--sm" type="button"
    whileTap=${{ scale: 0.97 }} transition=${SPRING}
    onClick=${()=>go("/ops")}>Back to Operations</${M.button}>`;
  const retry = html`<${M.button} key="r" className="btn btn--go btn--sm" type="button"
    whileTap=${{ scale: 0.97 }} transition=${SPRING}
    onClick=${()=>{ toast("Reloading console"); hardReload(); }}>
    <${Ico} d="refresh" w=${13}/> Try again</${M.button}>`;
  switch(s.id){
    case "boot":
      return [
        html`<button key="w" className="btn btn--ghost btn--sm" type="button" disabled>
          Waiting for readiness</button>`,
        html`<${M.button} key="c" className="btn btn--go btn--sm" type="button"
          whileTap=${{ scale: 0.97 }} transition=${SPRING}
          onClick=${()=>{ toast("Re-checking readiness"); hardReload(); }}>
          <${Ico} d="refresh" w=${13}/> Re-check</${M.button}>`,
      ];
    case "400":
      return [retry, back];
    case "401":
      return [html`<${M.button} key="s" className="btn btn--go btn--sm" type="button"
        whileTap=${{ scale: 0.97 }} transition=${SPRING}
        onClick=${()=>go("/auth/login")}>Sign in again</${M.button}>`, back];
    case "403":
      return [
        back,
        html`<${M.button} key="p" className="btn btn--ghost btn--sm" type="button"
          whileTap=${{ scale: 0.97 }} transition=${SPRING}
          onClick=${()=>{ toast("Opening Profile"); go("/settings/profile"); }}>
          Open Profile</${M.button}>`,
      ];
    case "404":
      return [back];
    case "409":
      return [
        retry,
        html`<${M.button} key="p" className="btn btn--ghost btn--sm" type="button"
          whileTap=${{ scale: 0.97 }} transition=${SPRING}
          onClick=${()=>{ toast("Re-reading latest state"); hardReload(); }}>
          Re-read latest</${M.button}>`,
      ];
    case "429":
      return [html`<${Countdown} key="c" toast=${toast}/>`];
    case "503":
    case "500":
    case "502":
      return [retry, back];
    case "offline":
      return [
        retry,
        html`<${M.button} key="f" className="btn btn--ghost btn--sm" type="button"
          whileTap=${{ scale: 0.97 }} transition=${SPRING}
          onClick=${()=>{ toast("Opening Operations"); go("/ops"); }}>
          Open Operations</${M.button}>`,
      ];
    default:
      return [retry, back];
  }
}

function Countdown({ toast }){
  const [n,setN] = useState(118);
  useEffect(()=>{ const id = setInterval(()=>setN(v=>v>0?v-1:0),1000); return ()=>clearInterval(id); },[]);
  const unlocked = n <= 0;
  return html`<button className="btn btn--ghost btn--sm" type="button"
      disabled=${!unlocked}
      onClick=${()=>{
        if (!unlocked) return;
        toast("Rate limit cleared · reloading");
        hardReload();
      }}>
    ${unlocked
      ? "Try again"
      : `Unlocks in ${String(Math.floor(n/60)).padStart(2,"0")}:${String(n%60).padStart(2,"0")}`}</button>`;
}
